import {
  Input,
  Output,
  inject,
  ViewChild,
  Component,
  forwardRef,
  DestroyRef,
  TemplateRef,
  EventEmitter,
  SimpleChanges,
  AfterViewInit,
  ViewContainerRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { Store } from '@ngrx/store';
import { TemplatePortal } from '@angular/cdk/portal';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { closeBrackets, closeBracketsKeymap } from '@codemirror/autocomplete';
import {
  keymap,
  Decoration,
  EditorView,
  ViewPlugin,
  ViewUpdate,
  lineNumbers,
  DecorationSet,
  drawSelection,
  highlightActiveLine,
  highlightSpecialChars,
  highlightActiveLineGutter,
} from '@codemirror/view';
import {
  Extension,
  EditorState,
  StateEffect,
  Transaction,
  EditorSelection,
  RangeSetBuilder,
} from '@codemirror/state';
import {
  LRLanguage,
  foldGutter,
  foldKeymap,
  indentUnit,
  syntaxTree,
  LanguageSupport,
  bracketMatching,
} from '@codemirror/language';
import {
  history,
  defaultKeymap,
  historyKeymap,
  indentWithTab,
} from '@codemirror/commands';
import { lintKeymap } from '@codemirror/lint';
import { searchKeymap } from '@codemirror/search';
import { styleTags, tags } from '@lezer/highlight';

import {
  AutocompleteContext,
  AutocompleteService,
} from '../../services/autocomplete.service';
import { LoggerService } from '@services/logger.service';
import { EscapeManager } from '@sui/shared';
import { FeedService } from '../../../core/services/feed.service';
import { NomenclatureService } from 'src/app/core/services/nomenclature.service';
import {
  AutocompleteSuggestion,
  TypeaheadDropdownComponent,
} from './typeahead-dropdown/typeahead-dropdown.component';

import {
  ContextDetector,
  ContextChangeEvent,
  createContextDetector,
} from './context-detector';

import { parser } from './solr-parser';
import { errorHighlightField, setErrorEffect } from './error-highlighter';
import { filterTag, sensikaDarkTheme, sensikaLightTheme } from './theme/theme';

import { NomObjectTypes, EscapePriority } from '../../../core/constants';

import { selectCoreDarkMode } from 'src/app/core/state';

import { tap } from 'rxjs';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { AsyncPipe } from '@angular/common';

// * COMMENTED OUT: Original CodeMirror autocomplete functionality
// * Keeping types available for existing methods that will be refactored

@Component({
  selector: 'app-query-editor',
  templateUrl: './query-editor.component.html',
  styleUrls: ['./query-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => QueryEditorComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FaIconComponent, TypeaheadDropdownComponent, AsyncPipe],
})
export class QueryEditorComponent
  implements AfterViewInit, ControlValueAccessor
{
  @ViewChild('queryEditor') queryEditor: any;
  @ViewChild('typeaheadDropdownTemplate')
  typeaheadDropdownTemplate!: TemplateRef<any>;

  @Input()
  set query(val: string) {
    if (val !== this._query) {
      this._query = val;
      this.updateEditorContent(val);
    }
  }
  @Input() objectTypeId = NomObjectTypes.ONLINE_FEED;
  @Input() isReadOnly: boolean = false;
  @Input() ctrlEnterExtension: Extension = null;
  @Input() handleQueryInputChanges: boolean = true;

  @Output() queryChange = new EventEmitter<string>();
  @Output() queryBlur = new EventEmitter<void>();

  // Internal data model
  private _query: string = '';

  // Placeholders for the callbacks which are later provided by the Control Value Accessor
  private onTouched: () => void = () => {};
  private onChange: (query: string) => void = () => {};

  private view!: EditorView;

  private isDarkMode = false;
  // * WILL BE REFACTORED: These completion-related properties will be moved to AutocompleteService
  // * Currently preserved for reference and gradual migration
  private propertyNameCompletions = [];

  isFullscreen = false;

  private destroyRef = inject(DestroyRef);
  private logger = inject(LoggerService);
  private overlay = inject(Overlay);
  private viewContainerRef = inject(ViewContainerRef);
  private escapeManager = inject(EscapeManager);

  // * NEW: Make autocomplete service public for template access
  public autocompleteService = inject(AutocompleteService);

  // * NEW: Overlay management
  private overlayRef: OverlayRef | null = null;
  private portal: TemplatePortal | null = null;
  private escapeHandlerId: symbol | null = null;

  private programmaticEdit = false;
  private firstLoadSkipped = false;
  private initialQueryValue: string = ''; // * baseline value for diff-based change detection

  constructor(
    private nomenclatureService: NomenclatureService,
    private feedService: FeedService,
    private store: Store,
  ) {
    this.store
      .select(selectCoreDarkMode)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((isDarkMode: boolean) => {
          this.isDarkMode = isDarkMode;
          this.updateTheme();
        }),
      )
      .subscribe();
  }

  private initializing = false;
  private initialized = false;

  private async initializeEditorOnce(): Promise<void> {
    if (this.initialized || this.initializing) return;
    this.initializing = true;
    try {
      await this.initializeEditor();
      this.initialized = true;
    } finally {
      this.initializing = false;
    }
  }

  ngAfterViewInit(): void {
    // * Configure the autocomplete service with backend services
    this.autocompleteService.configureServices(
      this.nomenclatureService,
      this.feedService,
    );

    // * FIXED: Set up single persistent subscription for suggestions
    this.setupAutocompleteSubscription();

    // * Set up overlay management for typeahead dropdown
    this.setupOverlayManagement();

    this.initializeEditorOnce()
      .then(() => {
        if (this.view) {
          this.subscribeToContentChanges();
          // * Load property completions for autocomplete
          this.autocompleteService.loadPropertyCompletions(this.objectTypeId);

          if (this.isReadOnly) {
            this.setReadOnly(true);
          }
        }
      })
      .catch((error) => {});
  }

  ngOnChanges(changes: SimpleChanges): void {
    // * Handle changes to the query input (should not update editor content when changes come from Advanced Search - it breaks the the input)
    if (this.handleQueryInputChanges && this.view && this._query) {
      this.updateEditorContent(this._query);
    }

    if (this.isReadOnly) {
      this.setReadOnly(true);
    }
  }

  // * NEW: Set up single persistent subscription for autocomplete suggestions
  private setupAutocompleteSubscription(): void {
    this.autocompleteService.suggestions$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((suggestions) => {
          // * FIXED: Only process suggestions when we have an active context and not hiding
          if (
            !this.currentAutocompleteContext ||
            !this.currentEditorView ||
            this.isHidingDropdown
          ) {
            // * Ignore suggestions when no active context or hiding
            return;
          }

          if (suggestions.length > 0) {
            // * Calculate position and show dropdown only when we have suggestions
            this.autocompleteService
              .calculateDropdownPosition(
                this.currentEditorView,
                this.currentAutocompleteContext.cursorPosition,
              )
              .then((position) => {
                if (!this.isHidingDropdown) {
                  // Double-check before showing
                  this.autocompleteService.showDropdown(position);
                  // console.log(
                  //   '🎯 Showing dropdown with',
                  //   suggestions.length,
                  //   'suggestions',
                  // );
                }
              })
              .catch((error) => {
                console.warn('Failed to calculate dropdown position:', error);
              });
          } else {
            // * STEP 3 FIX: Don't hide dropdown for manual trigger with valid context but no suggestions
            // * This prevents infinite loop when manual trigger finds no suggestions
            if (!this.isHidingDropdown && !this.isManuallyTriggered) {
              this.autocompleteService.hideDropdown();
              // console.log(
              //   '❌ No suggestions for automatic search, hiding dropdown',
              // );
            } else if (this.isManuallyTriggered) {
              // console.log(
              //   '⚠️ Manual trigger: No suggestions found but keeping dropdown state',
              // );
            }
          }
        }),
      )
      .subscribe();
  }

  // * Set up overlay management for dropdown portal
  private setupOverlayManagement(): void {
    // * Subscribe to isVisible$ to create/destroy overlay
    this.autocompleteService.isVisible$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((isVisible) => {
          if (isVisible) {
            this.createOverlay();
          } else {
            this.destroyOverlay();
          }
        }),
      )
      .subscribe();

    // * update overlay position when visible
    this.autocompleteService.position$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((position) => {
          if (this.overlayRef && position) {
            this.updateOverlayPosition(position);
          }
        }),
      )
      .subscribe();
  }

  // * Create overlay and attach portal
  private createOverlay(): void {
    if (this.overlayRef) {
      return;
    }

    const positionStrategy = this.overlay
      .position()
      .global()
      .top('0px')
      .left('0px');

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.noop(),
      hasBackdrop: false,
    });

    // * Register escape handler for overlay content (highest priority)
    this.escapeHandlerId = this.escapeManager.register(() => {
      if (this.autocompleteService.isVisible()) {
        if (!this.isHidingDropdown) {
          this.hideDropdownAndCleanup();
        }
        return true; // ! handled
      }

      return false; // ! not handled, let other handlers run
    }, EscapePriority.OVERLAY_CONTENT);

    this.portal = new TemplatePortal(
      this.typeaheadDropdownTemplate,
      this.viewContainerRef,
    );

    this.overlayRef.attach(this.portal);
  }

  private updateOverlayPosition(position: {
    top: number;
    left: number;
    maxHeight?: number;
  }): void {
    if (!this.overlayRef) {
      return;
    }

    const positionStrategy = this.overlayRef.getConfig()
      .positionStrategy as any;
    positionStrategy.top(`${position.top}px`).left(`${position.left}px`);
    this.overlayRef.updatePosition();
  }

  private destroyOverlay(): void {
    // * Unregister escape handler
    if (this.escapeHandlerId) {
      this.escapeManager.unregister(this.escapeHandlerId);
      this.escapeHandlerId = null;
    }

    if (this.overlayRef) {
      this.overlayRef.detach();
      this.overlayRef.dispose();
      this.overlayRef = null;
      this.portal = null;
    }
  }

  // * Helper to hide dropdown and clean up state
  private hideDropdownAndCleanup(): void {
    this.isHidingDropdown = true;
    this.autocompleteService.hideDropdown();
    this.currentAutocompleteContext = null;
    this.currentEditorView = null;
    this.clearManualTrigger();
    setTimeout(() => (this.isHidingDropdown = false), 100);
  }

  // * WILL BE REFACTORED: This method will be integrated with the new AutocompleteService
  // * Currently preserved for reference and gradual migration
  private async loadCompletions(): Promise<void> {
    try {
      const response = await this.feedService
        .getFeedOperatorHints(this.objectTypeId)
        .toPromise();

      response.forEach((element: any) => {
        this.propertyNameCompletions.push({
          label: element.name,
          type: 'keyword',
          info: element.description,
          apply: `${element.name}:`,
        });
      });
    } catch (error) {
      // ! Handle errors if needed
      console.error('Error fetching operator hints:', error);
    }
  }

  // * WILL BE REFACTORED: This context detection logic will be extracted to a CodeMirror extension
  // * and integrated with the new Angular autocomplete system
  // * Currently preserved for reference and gradual migration

  private readonly filterPlugin = ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;
      constructor(view: EditorView) {
        this.decorations = this.buildDecorations(view);
      }
      update(update: ViewUpdate) {
        if (update.docChanged || update.viewportChanged) {
          this.decorations = this.buildDecorations(update.view);
        }
      }
      buildDecorations(view: EditorView): DecorationSet {
        const builder = new RangeSetBuilder<Decoration>();
        syntaxTree(view.state).iterate({
          enter(node) {
            if (node.name === 'Dimension') {
              builder.add(
                node.from,
                node.to,
                Decoration.mark({ class: 'cm-query-dimension' }),
              );
            }
          },
        });
        return builder.finish();
      }
    },
    { decorations: (v) => v.decorations },
  );

  private readonly propertyNamePlugin = ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;
      constructor(view: EditorView) {
        this.decorations = this.buildDecorations(view);
      }
      update(update: ViewUpdate) {
        if (update.docChanged || update.viewportChanged) {
          this.decorations = this.buildDecorations(update.view);
        }
      }
      buildDecorations(view: EditorView): DecorationSet {
        const builder = new RangeSetBuilder<Decoration>();
        syntaxTree(view.state).iterate({
          enter(node) {
            if (node.name === 'PropertyName') {
              builder.add(
                node.from,
                node.to,
                Decoration.mark({ class: 'cm-query-property-name' }),
              );
            }
          },
        });
        return builder.finish();
      }
    },
    { decorations: (v) => v.decorations },
  );

  private async initializeEditor(): Promise<void> {
    try {
      await this.loadCompletions();
      const hostElement = this.queryEditor.nativeElement;

      const QueryLanguage = LRLanguage.define({
        parser: parser.configure({
          props: [
            styleTags({
              PropertyName: tags.propertyName,
              String: tags.string,
              Quoted: tags.string,
              UnQuoted: tags.string,
              Operator: tags.operator,
              And: tags.operator,
              Or: tags.operator,
              Not: tags.operator,
              Near: tags.operator,
              Next: tags.operator,
              ProximityOperator: tags.operator,
              Dimension: filterTag,
              Comment: tags.lineComment,
              BlockComment: tags.blockComment,
              Hex: tags.regexp,
            }),
          ],
        }),
      });

      const languageSupport = new LanguageSupport(QueryLanguage);

      const theme = this.isDarkMode ? sensikaDarkTheme : sensikaLightTheme;

      const extensions = [
        // * Core editor features preserved manually (instead of basicSetup)
        lineNumbers(),
        highlightActiveLineGutter(),
        highlightSpecialChars(),
        drawSelection(),
        highlightActiveLine(),
        history(),
        foldGutter(),
        // ! maxScanDistance is measured in characters (document positions), not lines
        // ! Increased from the default (10_000) because bracket highlighting breaks on large queries
        bracketMatching({ maxScanDistance: 200_000 }),
        closeBrackets(),

        // * Place our custom autocomplete keymap
        this.createAutocompleteKeymap(),

        this.customEnterKeymap(),
        this.customCtrlArrowKeymap(),

        // Add CTRL + ENTER extension if available
        ...(this.ctrlEnterExtension ? [this.ctrlEnterExtension] : []),

        // * Apply all keymaps in correct controlled order
        keymap.of([
          ...defaultKeymap,
          ...searchKeymap,
          ...historyKeymap,
          ...foldKeymap,
          ...closeBracketsKeymap,
          ...lintKeymap,
        ]),

        languageSupport,
        theme,
        this.filterPlugin,
        this.propertyNamePlugin,
        errorHighlightField,
        EditorView.lineWrapping, // ✅ Enable line wrapping

        EditorView.domEventHandlers({
          blur: () => {
            this.queryBlur.emit();
            return false;
          },
        }),

        // * Custom context detection extension for Angular autocomplete
        ...this.createContextDetectorExtension(),

        // * COMMENTED OUT: Original CodeMirror completion keymap
        // * Will be replaced with custom Angular autocomplete handling
        // ...completionKeymap,
        // {
        //   key: 'Tab',
        //   run: acceptCompletion,
        //   preventDefault: true,
        // },

        // Change listener — diff-based detection to prevent false dirty state
        // Emit every value if not the first (populating one) and initial is not ""
        EditorView.updateListener.of((update: ViewUpdate) => {
          if (!update.docChanged) return;
          const currentValue = update.state.doc.toString();

          this.clearEditorErrors();

          if (!this.firstLoadSkipped && this.initialQueryValue !== '') {
            // ignore the very first load
            this.firstLoadSkipped = true;
            return;
          }

          // emit every subsequent change, even if same as initial
          this.onQueryChange(currentValue);
        }),

        // ✅ Add this for read-only mode
        //EditorView.editable.of(this.isReadOnly),
      ];

      try {
        const state = EditorState.create({
          doc: '',
          extensions: [
            extensions,
            this.protectReadOnlyExtension(),
            keymap.of([indentWithTab]),
            indentUnit.of('\t'),
            EditorState.tabSize.of(2),
          ],
        });

        this.view = new EditorView({
          state,
          parent: hostElement,
        });
      } catch (e) {
        console.log(e);
      }
    } catch (e) {
      console.error(e);
    }
  }

  private subscribeToContentChanges(): void {
    if (this.view) {
      this.view.dispatch({
        changes: {
          from: 0,
          to: this.view.state.doc.length,
          insert: this._query,
        },
      });
    }
  }

  // The method set in registerOnChange, it is just a placeholder for a method that takes one parameter,
  // we use it to emit changes back to the form
  writeValue(query: string): void {
    this.logger.debug(
      'QueryEditorComponent writeValue called with query:',
      query,
    );
    this._query = query;
    this.initialQueryValue = query || ''; // * store baseline for diff-based change detection
    // this.logger.debug('QueryEditorComponent writeValue', query);
    this.subscribeToContentChanges();
  }

  // Allows Angular to register a function to call when the model (query) changes
  registerOnChange(fn: (query: string) => void): void {
    this.onChange = fn;
  }

  // Allows Angular to register a function to call when the component has been touched
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  // Call this method when the query changes in the editor
  private onQueryChange(newQuery: string): void {
    this._query = newQuery;
    this.onChange(newQuery);
    this.onTouched();
    this.queryChange.emit(newQuery); // Emit the change event
  }

  private updateEditorContent(query: string): void {
    if (!this.view) return;
    this.programmaticEdit = true;
    //console.log('Updating editor content programmatically:', query);
    this.view.dispatch({
      changes: { from: 0, to: this.view.state.doc.length, insert: query },
      annotations: Transaction.userEvent.of('setDoc'),
    });
    this.programmaticEdit = false;
  }

  public displayEditorError(
    line: number,
    message: string,
    column: number,
  ): void {
    if (!this.view) return;

    const doc = this.view.state.doc;
    const lineInfo = this.view.state.doc.line(line);
    const isNearEnd = lineInfo.to > doc.length - 200;

    // Calculate cursor position
    const columnOffset = Math.max(0, column);
    const position = Math.min(lineInfo.from + columnOffset, lineInfo.to);

    this.view.dispatch({
      selection: EditorSelection.cursor(position),
      scrollIntoView: true,
      effects: setErrorEffect.of({ line, message, column }),
    });

    this.view.dispatch({
      effects: EditorView.scrollIntoView(lineInfo.from, {
        y: isNearEnd ? 'end' : 'center',
      }),
    });
    this.logger.error(`Error on line ${line}: ${message}`);

    this.view.focus();
  }

  public clearEditorErrors(): void {
    if (!this.view) return;
    this.view.dispatch({
      effects: setErrorEffect.of(null), // Clear all errors
    });
    this.logger.debug('Cleared all editor errors');
  }
  // * Updates the editor content to have a minimum number of lines.

  // * Updates the editor to have a minimum number of lines.
  // * If the current number of lines is less than the specified minimum number of lines,
  // * it appends new lines to the editor until the minimum number of lines is reached.
  // *
  // * @param editor - The editor instance.
  // * @param minNumOfLines - The minimum number of lines to be present in the editor.
  updateToMinNumberOfLines(editor, minNumOfLines) {
    const currentNumOfLines = editor.state.doc.lines;
    const currentStr = editor.state.doc.toString();

    if (currentNumOfLines >= minNumOfLines) {
      return;
    }

    const lines = minNumOfLines - currentNumOfLines;
    const cursorPosition = currentStr.length;
    const appendLines = '\n'.repeat(lines);

    editor.dispatch({
      changes: { from: currentStr.length, insert: appendLines },
      selection: { anchor: cursorPosition },
    });
  }

  insertStringAtCursor(str: string) {
    if (this.view) {
      const tr = this.view.state.update({
        changes: {
          from: this.view.state.selection.main.head,
          insert: str,
        },
      });

      const transactions: any = [tr];
      this.view.dispatch(transactions);
    }
  }

  setQuery(query: string) {
    this.updateEditorContent(query);
    //this.writeValue(query);
  }

  addOperator(hint: string) {
    this.insertStringAtCursor(hint);
  }

  getReadOnlyRanges(doc: string): { from: number; to: number }[] {
    const ranges: { from: number; to: number }[] = [];
    const regex = /《(.*?)》/gs;
    let match;

    while ((match = regex.exec(doc)) !== null) {
      ranges.push({
        from: match.index,
        to: match.index + match[0].length,
      });
    }

    return ranges;
  }

  protectReadOnlyExtension(): any {
    return EditorState.transactionFilter.of((tr: Transaction) => {
      if (tr.annotation(Transaction.userEvent) === 'setDoc') return tr;
      if (this.programmaticEdit) return tr;

      const doc = tr.startState.doc.toString();
      const ranges = this.getReadOnlyRanges(doc);
      let isBlocked = false;
      let decorations = [];

      tr.changes.iterChanges((fromA, toA, fromB, toB, inserted) => {
        // Iterate over Ranges to check if any changes fall into Protected Ranges
        for (let r of ranges) {
          if (fromA < r.to && toA > r.from) {
            isBlocked = true;

            // Add a Decoration for the Protected Range
            decorations.push(
              Decoration.mark({ class: 'cm-readonly' }).range(r.from, r.to),
            );
          }
        }
      });

      // If there are any protected areas, add the decorations to the transaction
      if (decorations.length > 0) {
      }

      // Block the changes if any overlap a protected range
      return isBlocked ? [] : tr;
    });
  }

  private updateTheme(): void {
    if (!this.view) return;

    // * store the current content
    const currentContent = this.view.state.doc.toString();

    this.view.destroy();

    // * reinit the editor with the new theme
    this.initializeEditor().then(() => {
      this.updateEditorContent(currentContent);
    });
  }

  // * NEW: Store current autocomplete context for keyboard navigation
  private currentAutocompleteContext: AutocompleteContext | null = null;

  // * NEW: Store current editor view for autocomplete positioning
  private currentEditorView: EditorView | null = null;

  // * FIXED: Flag to prevent multiple hideDropdown calls
  private isHidingDropdown = false;

  // * NEW: Manual trigger state management
  private isManuallyTriggered = false;

  // * NEW: Store reference to context detector for manual triggering
  private contextDetector: ContextDetector | null = null;

  // * NEW: Create keyboard navigation extension
  private createAutocompleteKeymap() {
    return keymap.of([
      {
        key: 'ArrowDown',
        run: (view: EditorView) => {
          if (this.autocompleteService.isVisible()) {
            this.autocompleteService.navigateDown();
            // ! Prevent default
            return true;
          }
          // ! Allow default behavior
          return false;
        },
      },
      {
        key: 'ArrowUp',
        run: (view: EditorView) => {
          if (this.autocompleteService.isVisible()) {
            this.autocompleteService.navigateUp();
            // ! Prevent default
            return true;
          }
          // ! Allow default behavior
          return false;
        },
      },
      {
        key: 'Enter',
        run: (view: EditorView) => {
          const isVisible = this.autocompleteService.isVisible();
          const selectedSuggestion =
            this.autocompleteService.getSelectedSuggestion() ??
            this.autocompleteService.getCurrentSuggestions()[0] ?? // accept suggestion when one item is left
            null;

          if (
            isVisible &&
            selectedSuggestion &&
            this.currentAutocompleteContext
          ) {
            // console.log('⏎ Accepting suggestion:', selectedSuggestion.label);
            // ! Accept suggestion without calling acceptCurrentSuggestion (which hides dropdown)
            this.acceptSuggestionWithSmartReplacement(selectedSuggestion);
            this.hideDropdownAndCleanup();
            // ! Prevent default
            return true;
          }
          // ! Allow default behavior
          return false;
        },
      },
      {
        key: 'Tab',
        run: (view: EditorView) => {
          const isVisible = this.autocompleteService.isVisible();
          const selectedSuggestion =
            this.autocompleteService.getSelectedSuggestion();

          if (
            isVisible &&
            selectedSuggestion &&
            this.currentAutocompleteContext
          ) {
            this.acceptSuggestionWithSmartReplacement(selectedSuggestion);
            this.hideDropdownAndCleanup();
            // ! Prevent default
            return true;
          }

          // ! Allow default behavior
          return false;
        },
      },
      {
        key: 'Escape',
        run: (view: EditorView) => {
          if (this.autocompleteService.isVisible() && !this.isHidingDropdown) {
            this.hideDropdownAndCleanup();
            // ! Prevent default
            return true;
          }
          // ! Allow default behavior
          return false;
        },
      },
      {
        key: 'Mod-Space',
        run: (view: EditorView) => {
          this.triggerManualAutocomplete(view);
          // ! Prevent default
          return true;
        },
      },
    ]);
  }

  private customEnterKeymap() {
    return keymap.of([
      {
        key: 'Enter',
        run: (view) => {
          const { state } = view;
          const { from } = state.selection.main;

          // ! Block Enter anywhere at or after the first Dimension (between dimensions and after the last)
          let firstDimensionStart = -1;
          syntaxTree(state).iterate({
            enter(node) {
              if (node.name === 'Dimension' && firstDimensionStart === -1)
                firstDimensionStart = node.from;
            },
          });
          if (firstDimensionStart !== -1 && from >= firstDimensionStart)
            return true;

          let nextLine = null;
          const prevLine = state.doc.lineAt(from);
          if (prevLine.number < state.doc.lines) {
            nextLine = state.doc.line(prevLine.number + 1);
          }

          const prevIndent = prevLine.text.match(/^\s*/)?.[0] ?? '';
          const nextIndent = nextLine?.text.match(/^\s*/)?.[0] ?? '';

          let indentToUse = prevIndent;

          if (nextLine) {
            const nextStartsWithClosingParen = nextLine.text
              .trimStart()
              .startsWith(')');

            if (!nextStartsWithClosingParen) {
              indentToUse = nextIndent;
            }
          }

          view.dispatch({
            changes: { from, insert: '\n' + indentToUse },
            selection: { anchor: from + 1 + indentToUse.length },
          });

          return true;
        },
      },
    ]);
  }

  // * NEW: Manual trigger state management methods
  private setManuallyTriggered(value: boolean): void {
    this.isManuallyTriggered = value;
  }

  private clearManualTrigger(): void {
    this.isManuallyTriggered = false;
  }

  // * NEW: Create context detector extension with manual trigger support
  private createContextDetectorExtension(): any[] {
    const [field, plugin, detector] = createContextDetector(
      this.onContextChange.bind(this), // !            callback to notify our app
      () => this.isManuallyTriggered, // !             Pass manual trigger state getter
      () => this.autocompleteService.isVisible(), // ! Pass dropdown visibility state getter
    );

    // * Store detector reference for manual triggering
    this.contextDetector = detector;

    return [field, plugin];
  }

  // * NEW: Trigger manual autocomplete on Ctrl+Space
  private triggerManualAutocomplete(view: EditorView): void {
    // console.log('🔥 Triggering manual autocomplete...');

    // * Set manual trigger flag
    this.setManuallyTriggered(true);

    // * Force context detection using stored detector reference
    if (this.contextDetector) {
      this.contextDetector.forceContextDetection(view);
      // console.log('🎯 Forced context detection completed');
    }
  }

  // * NEW: Smart suggestion acceptance with text replacement
  private acceptSuggestionWithSmartReplacement(
    suggestion: AutocompleteSuggestion,
  ): void {
    if (!this.view || !this.currentAutocompleteContext) return;

    // console.log('🎯 Accepting suggestion with smart replacement:', suggestion);

    // * Use the new smart replacement logic from AutocompleteService
    this.autocompleteService.acceptSuggestion(
      this.view,
      suggestion,
      this.currentAutocompleteContext,
    );
  }

  // * NEW: Handle context changes from the context detector extension
  private onContextChange(event: ContextChangeEvent): void {
    const { context, shouldShowAutocomplete, editorView } = event;

    if (shouldShowAutocomplete && context) {
      // * Convert TypingContext to AutocompleteContext
      const autocompleteContext: AutocompleteContext = {
        searchTerm: context.searchTerm,
        propertyName: context.propertyName,
        isPropertyValue: context.isPropertyValue,
        cursorPosition: context.cursorPosition,
        syntaxTreeContext: context.syntaxTreeContext,
      };

      // * Store context and editor view for the persistent subscription
      this.currentAutocompleteContext = autocompleteContext;
      this.currentEditorView = editorView;
      this.isHidingDropdown = false; // Reset flag when starting new search

      // * STEP 4 FIX: Use forceSearch for manual triggers to bypass distinctUntilChanged
      if (this.isManuallyTriggered) {
        // ! this fixes the case when we want to reopen the Dropdown
        this.autocompleteService.forceSearch(autocompleteContext);
        // console.log('🔥 Triggered FORCED autocomplete search (manual):');
      } else {
        this.autocompleteService.search(autocompleteContext);
        // console.log('🔍 Triggered autocomplete search (automatic):');
      }
    } else {
      // * STEP 3 FIX: Only clear manual trigger state when context is truly invalid
      // * Don't clear it when manual trigger is active but context detection runs automatically
      if (!this.isHidingDropdown) {
        this.currentAutocompleteContext = null;
        this.currentEditorView = null;
        this.autocompleteService.hideDropdown();
      }
    }
  }

  // * NEW: Handle suggestion selection from Angular dropdown
  onSuggestionSelected(suggestion: AutocompleteSuggestion): void {
    if (!this.view || !this.currentAutocompleteContext) return;

    // console.log('✅ Suggestion selected (mouse):', suggestion);

    // * Use smart replacement logic
    this.acceptSuggestionWithSmartReplacement(suggestion);
    this.hideDropdownAndCleanup();
  }

  // * NEW: Handle suggestion hover for keyboard navigation
  onSuggestionHovered(index: number): void {
    // console.log('🖱️ Mouse hovered over item:', index);
    this.autocompleteService.setSelectedIndex(index);
  }

  // * NEW: Handle dropdown close
  onDropdownClosed(): void {
    if (!this.isHidingDropdown) {
      this.hideDropdownAndCleanup();
    }
  }

  // Optional: a method to toggle readonly dynamically
  setReadOnly(isReadOnly: boolean): void {
    this.view?.dispatch({
      effects: StateEffect.appendConfig.of(EditorView.editable.of(!isReadOnly)),
    });
    // Toggle the CSS class
    if (isReadOnly) {
      this.view?.dom?.classList.add('cm-readonly');
    } else {
      this.view?.dom?.classList.remove('cm-readonly');
    }
  }

  toggleFullscreen() {
    this.isFullscreen = !this.isFullscreen;
    setTimeout(() => {
      this.view?.requestMeasure(); // Recalculate layout
    }, 0);
  }

  findPrevBracket(doc: string, from: number): number | null {
    for (let i = from - 1; i >= 0; i--) {
      if (doc[i] === '(' || doc[i] === ')') return i;
    }
    return null;
  }

  findNextBracket(doc: string, from: number): number | null {
    for (let i = from; i < doc.length; i++) {
      if (doc[i] === '(' || doc[i] === ')') return i;
    }
    return null;
  }

  customCtrlArrowKeymap() {
    return keymap.of([
      {
        key: 'Ctrl-ArrowDown',
        run: (view) => {
          const pos = view.state.selection.main.head;
          const doc = view.state.doc.toString();
          const next = this.findNextBracket(doc, pos + 1);
          if (next !== null) {
            view.dispatch({
              selection: EditorSelection.cursor(next),
              scrollIntoView: true,
            });
            return true;
          }
          return false;
        },
      },
      {
        key: 'Ctrl-ArrowUp',
        run: (view) => {
          const pos = view.state.selection.main.head;
          const doc = view.state.doc.toString();
          const prev = this.findPrevBracket(doc, pos);
          if (prev !== null) {
            view.dispatch({
              selection: EditorSelection.cursor(prev),
              scrollIntoView: true,
            });
            return true;
          }
          return false;
        },
      },
    ]);
  }
}
